import { IOrder } from "@portal/types/api/IOrder";
import { IOrderExtended } from "@portal/types/orders";
import { GetOrderStatus } from "@portal/components/pages/orders/helper";
import { broadcastUserNotification } from "@portal/components/molecules/userNotification/userMessageChannel";
import { hasOrderNotificationCookie } from "@portal/helpers/orderNotificationCookieHelper";
import { HubConnection } from "@microsoft/signalr";
import { registerStockObserverForOrder } from "@portal/infront/stockData";

export type OrdersSnapshot = {
  orders: IOrderExtended[];
  loading: boolean;
};

let orders: IOrderExtended[] = [];
let loading = true;
const listeners = new Set<() => void>();
let snapshot: OrdersSnapshot = { orders, loading };

// Static server snapshot to satisfy useSyncExternalStore caching requirement
const serverSnapshot: OrdersSnapshot = { orders: [], loading: true };

const notifyListeners = () => listeners.forEach(listener => listener());

const updateSnapshot = () => {
  if (snapshot.orders !== orders || snapshot.loading !== loading) {
    snapshot = { orders, loading };
  }
};

const setStateAndNotify = () => {
  updateSnapshot();
  notifyListeners();
};

export const subscribeOrdersStore = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getOrdersSnapshot = (): OrdersSnapshot => {
  updateSnapshot();
  return snapshot;
};

export const getOrdersServerSnapshot = (): OrdersSnapshot => serverSnapshot;

export const markOrdersLoadingComplete = () => {
  loading = false;
  setStateAndNotify();
};

export const clearOrdersStore = () => {
  orders = [];
  loading = true;
  setStateAndNotify();
};

export const upsertOrder = (incomingOrder: IOrder) => {
  loading = false;

  const existingOrder = orders.find(order => order.orderNumber === incomingOrder.orderNumber);
  const isStockOrder = incomingOrder.orderLimit && incomingOrder.orderLimit > 0;

  if (isStockOrder) {
    const currentRest = existingOrder?.orderRest;

    if (
      hasOrderNotificationCookie(incomingOrder.orderNumber.toString()) &&
      currentRest &&
      incomingOrder.orderRest &&
      currentRest > incomingOrder.orderRest
    ) {
      broadcastUserNotification(
        incomingOrder.orderNumber,
        incomingOrder.orderQuantity
          ? `${incomingOrder.orderQuantity - incomingOrder.orderRest} av ${incomingOrder.orderQuantity} ${incomingOrder.tickerCode} aksjer handlet`
          : `${incomingOrder.tickerCode} aksjer handlet`,
      );
    }
  }

  orders = [
    ...orders.filter(order => order.orderNumber !== incomingOrder.orderNumber),
    {
      ...incomingOrder,
      orderType:
        incomingOrder.isin === "XX0000000002"
          ? ""
          : incomingOrder.orderQuantity && incomingOrder.orderQuantity < 0
            ? "Selg"
            : "Kjøp",
      statusText: GetOrderStatus(incomingOrder),
    },
  ];

  setStateAndNotify();
};

const streamOrdersHandler = () => ({
  next: async (item: IOrder) => {
    if (item.tickerCode === "Du har ingen ordre") {
      markOrdersLoadingComplete();
    } else {
      if (item.orderLimit && item.orderLimit > 0) {
        registerStockObserverForOrder(item);
      }
      upsertOrder(item);
    }
  },
  complete: () => {
    console.info("complete");
  },
  error: (err: unknown) => {
    console.error("err", err);
  },
});

export const streamOrdersToStore = async (connection: HubConnection | undefined, poaUser?: string, isAdvisor?: boolean) => {
  if (!connection) return;

  if (isAdvisor) {
    connection.stream<IOrder>("StreamOrdersWithAdvisor", poaUser ? poaUser : undefined).subscribe(streamOrdersHandler());
  } else if (poaUser) {
    connection.stream<IOrder>("StreamOrdersWithPoa", poaUser ? poaUser : undefined).subscribe(streamOrdersHandler());
  } else {
    connection.stream<IOrder>("StreamOrders").subscribe(streamOrdersHandler());
  }
};
